// 환불 처리 기록 — 주문 상세 (2026-10-16)
//
// 실제로 돌려준 돈(보증금·배송비·주문 취소)의 날짜·금액·수단을 남긴다. 매출 현황의 환불 집계는
// 이 기록만 본다(rental/services/refunds.py · sales.py).
// 기본값(종류별 금액·차감·수단·날짜)은 서버 refund_suggestions가 준다 — 화면은 그대로 채우기만 한다.
//   직접 수령·반납 주문은 회수 정산 환불액(보증금+배송비)이 두 줄(보증금 환불, 배송비 환불)로 온다.
import React, { useEffect, useState } from "react";

import api from "../../api";
import { formatWon } from "./format";

const KINDS = [
  ["DEPOSIT", "보증금 환불"],
  ["SHIPPING", "배송비 환불"],
  ["CANCEL", "주문 취소 환불"],
];
const METHODS = [
  ["CARD_PARTIAL", "카드 부분취소"],
  ["TRANSFER", "계좌이체"],
  ["OTHER", "기타"],
];
const today = () => new Date().toISOString().slice(0, 10);

function blankForm(order, suggestion) {
  return {
    kind: suggestion?.kind || "DEPOSIT",
    method: suggestion?.method || (order.payment_method === "cash" ? "TRANSFER" : "CARD_PARTIAL"),
    refunded_on: suggestion?.refunded_on || today(),
    amount: suggestion?.amount ?? "",
    deduction: suggestion?.deduction ?? "0",
    note: "",
    source: suggestion?.source || "",
  };
}

export default function RefundSection({ order, onSaved }) {
  const suggestions = order.refund_suggestions || [];
  const refunds = order.refunds || [];
  const [form, setForm] = useState(() => blankForm(order, suggestions[0]));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // 저장·삭제 뒤 주문이 바뀌면 남은 제안으로 다시 채운다.
  useEffect(() => {
    setForm(blankForm(order, (order.refund_suggestions || [])[0]));
  }, [order]);

  const show =
    refunds.length > 0 || suggestions.length > 0 ||
    ["PICKED_UP", "CANCELED"].includes(order.status) || order.needs_cancel_review;
  if (!show) return null;

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const changeKind = (e) => {
    const kind = e.target.value;
    const suggestion = suggestions.find((s) => s.kind === kind);
    setForm(suggestion ? blankForm(order, suggestion) : { ...blankForm(order), kind, amount: "" });
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const res = await api.post(`rental/orders/${order.id}/refunds/`, form);
      onSaved(res.data.order);
    } catch (e) {
      setError(e?.response?.data?.detail || "환불 기록을 저장하지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (refund) => {
    if (!window.confirm(`${refund.kind_label} ${formatWon(refund.amount)} (${refund.refunded_on}) 기록을 지울까요?`)) return;
    setSaving(true);
    setError("");
    try {
      const res = await api.post(`rental/orders/${order.id}/delete-refund/`, { refund_id: refund.id });
      onSaved(res.data.order);
    } catch (e) {
      setError(e?.response?.data?.detail || "환불 기록을 지우지 못했습니다.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="rental-section" style={{ marginBottom: 14 }}>
      <h3 className="rental-section-title">환불 처리 기록</h3>

      {refunds.length === 0 ? (
        <div className="rental-refund-empty">아직 기록이 없습니다.</div>
      ) : (
        <ul className="rental-refund-list">
          {refunds.map((r) => (
            <li key={r.id} className="rental-refund-item">
              <span className="rental-refund-text">
                {r.refunded_on} · {r.kind_label} <strong>{formatWon(r.amount)}</strong> · {r.method_label}
                {Number(r.deduction) > 0 && ` · 차감 ${formatWon(r.deduction)} (${r.note})`}
                {r.created_by_name && <span className="rental-refund-by"> — {r.created_by_name}</span>}
              </span>
              <button type="button" className="rental-refund-del" onClick={() => remove(r)} disabled={saving}
                      title="기록 삭제">
                ×
              </button>
            </li>
          ))}
        </ul>
      )}

      <div className="rental-refund-form">
        <div className="rental-row">
          <span className="rental-row-label">종류</span>
          <select className="rental-input rental-refund-field" value={form.kind} onChange={changeKind}>
            {KINDS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
          {form.source && <span className="rental-refund-source">기본값: {form.source}</span>}
        </div>
        <div className="rental-row">
          <span className="rental-row-label">환불 수단</span>
          <select className="rental-input rental-refund-field" value={form.method} onChange={set("method")}>
            {METHODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
          </select>
        </div>
        <div className="rental-row">
          <span className="rental-row-label">환불일</span>
          <input type="date" className="rental-input rental-refund-field" value={form.refunded_on}
                 onChange={set("refunded_on")} />
        </div>
        <div className="rental-row">
          <span className="rental-row-label">환불 금액</span>
          <input type="number" min="0" className="rental-input rental-refund-field" value={form.amount}
                 onChange={set("amount")} />
        </div>
        {form.kind === "DEPOSIT" && (
          <>
            <div className="rental-row">
              <span className="rental-row-label">차감 금액</span>
              <input type="number" min="0" className="rental-input rental-refund-field" value={form.deduction}
                     onChange={set("deduction")} />
            </div>
            {Number(form.deduction) > 0 && (
              <div className="rental-row">
                <span className="rental-row-label">차감 사유</span>
                <input className="rental-input" value={form.note} onChange={set("note")}
                       placeholder="필수 — 예: 범보의자 파손" />
              </div>
            )}
          </>
        )}
        {error && <div className="rental-error">{error}</div>}
        <div className="rental-modal-actions">
          <button type="button" className="rental-btn-confirm" onClick={save} disabled={saving}>
            {saving ? "저장 중" : "기록 저장"}
          </button>
        </div>
      </div>
    </div>
  );
}
